import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaBuilding,
  FaCheckCircle,
  FaEnvelope,
  FaKey,
  FaLock,
  FaShieldAlt,
  FaUser,
  FaUserPlus,
} from 'react-icons/fa';
import workspaceAPI from '../../services/workspaceAPI';
import { registerPushNotifications } from '../../services/pushNotificationService';
import { syncWorkspaceAccessFromResponse } from '../../utils/workspaceSession';
import './WorkspaceJoinPage.css';

const STEPS = [
  { icon: FaEnvelope, label: 'Open your invite email' },
  { icon: FaKey, label: 'Confirm workspace code' },
  { icon: FaUserPlus, label: 'Set your password & join' },
];

const WorkspaceJoinPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companyName, setCompanyName] = useState('');
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) setInviteCode(code.toUpperCase());
    const em = searchParams.get('email');
    if (em) setEmail(em);
  }, [searchParams]);

  useEffect(() => {
    if (inviteCode.trim().length >= 6) {
      workspaceAPI
        .getInvitePreview(inviteCode.trim())
        .then((res) => setCompanyName(res.data.companyName || ''))
        .catch(() => setCompanyName(''));
    } else {
      setCompanyName('');
    }
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await workspaceAPI.join({
        inviteCode: inviteCode.trim(),
        email: email.trim(),
        password,
        name: name.trim(),
      });
      if (res.data.success && res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        syncWorkspaceAccessFromResponse(res.data);
        window.dispatchEvent(new CustomEvent('codex-authenticated'));
        await registerPushNotifications({ requestPermission: true }).catch(() => {});
        navigate('/workspace', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to join');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ws-join-page">
      <div className="ws-join-bg" aria-hidden="true">
        <span className="ws-join-orb ws-join-orb-1" />
        <span className="ws-join-orb ws-join-orb-2" />
        <span className="ws-join-orb ws-join-orb-3" />
      </div>

      <div className="ws-join-shell">
        <aside className="ws-join-aside">
          <div className="ws-join-brand">
            <span className="ws-join-brand-icon">
              <FaBuilding />
            </span>
            <div>
              <p className="ws-join-brand-label">UBSAAPP Workspace</p>
              <h2 className="ws-join-brand-title">Join your team</h2>
            </div>
          </div>

          {companyName ? (
            <div className="ws-join-company-pill">
              <FaCheckCircle />
              <span>
                Invited to <strong>{companyName}</strong>
              </span>
            </div>
          ) : (
            <p className="ws-join-aside-lead">
              Enter the invite code from your company admin to access bookings, staff tools, and reports.
            </p>
          )}

          <ul className="ws-join-steps">
            {STEPS.map(({ icon: Icon, label }) => (
              <li key={label}>
                <span className="ws-join-step-icon">
                  <Icon />
                </span>
                {label}
              </li>
            ))}
          </ul>

          <div className="ws-join-trust">
            <FaShieldAlt />
            <span>Your email must be authorized by your company admin before you can join.</span>
          </div>
        </aside>

        <main className="ws-join-card">
          <header className="ws-join-card-header">
            <h1>Join Workspace</h1>
            <p>Use the invite from your company admin. Your email must be authorized first.</p>
          </header>

          {error && (
            <div className="ws-join-alert" role="alert">
              {error}
            </div>
          )}

          <form className="ws-join-form" onSubmit={handleSubmit}>
            <div className="ws-join-field">
              <label htmlFor="ws-invite-code">Workspace code</label>
              <div className="ws-join-input-wrap">
                <FaKey className="ws-join-input-icon" />
                <input
                  id="ws-invite-code"
                  className="ws-join-input ws-join-input-code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  placeholder="e.g. 2C9E6524"
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="ws-join-field">
              <label htmlFor="ws-work-email">Work email</label>
              <div className="ws-join-input-wrap">
                <FaEnvelope className="ws-join-input-icon" />
                <input
                  id="ws-work-email"
                  className="ws-join-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="ws-join-field">
              <label htmlFor="ws-your-name">Your name</label>
              <div className="ws-join-input-wrap">
                <FaUser className="ws-join-input-icon" />
                <input
                  id="ws-your-name"
                  className="ws-join-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  required
                  autoComplete="name"
                />
              </div>
            </div>

            <div className="ws-join-field-row">
              <div className="ws-join-field">
                <label htmlFor="ws-password">Password</label>
                <div className="ws-join-input-wrap">
                  <FaLock className="ws-join-input-icon" />
                  <input
                    id="ws-password"
                    className="ws-join-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    placeholder="Min. 6 characters"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="ws-join-field">
                <label htmlFor="ws-confirm-password">Confirm password</label>
                <div className="ws-join-input-wrap">
                  <FaLock className="ws-join-input-icon" />
                  <input
                    id="ws-confirm-password"
                    className="ws-join-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>

            <button type="submit" className="ws-join-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="ws-join-spinner" aria-hidden="true" />
                  Joining workspace…
                </>
              ) : (
                'Join workspace'
              )}
            </button>
          </form>

          <p className="ws-join-footer">
            Already have a password?{' '}
            <Link to="/login">Company login</Link>
          </p>
        </main>
      </div>
    </div>
  );
};

export default WorkspaceJoinPage;
